import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import Text from "@components/CustomText";
import ChevronRight from "@assets/svg/ChevronRight";
import color from "@constants/color";
import { width } from "@constants/metrics";
import { scale } from "@utils/scale";
import analytics from "@services/analytics";
import { GIGS_COPY } from "@screens/Gigs/copy";
import {
  formatGigDayLabel,
  getHomeWidgetDotColor,
  localTodayIso,
} from "@screens/Gigs/utils";
import type {
  GigsHomeWidget,
  GigsNavigation,
  HomeWidgetDayStatus,
} from "@screens/Gigs/types";
import LoadingComponent from "./LoadingComponent";

interface GigsWidgetProps {
  item: GigsHomeWidget;
}

const STATUS_LABELS: Record<HomeWidgetDayStatus, string> = {
  ON_DUTY: GIGS_COPY.status.onDuty,
  PENDING: GIGS_COPY.status.pending,
  OFF: GIGS_COPY.status.off,
  NOT_MARKED: GIGS_COPY.status.notMarked,
};

/**
 * Home-screen summary card for widget_type "GIGS". Purely presentational —
 * data flows in through the existing `homePageWidget` redux slice / generic
 * home-widget saga, same as every other widget; this component never fetches
 * on its own. Header, chevron and divider match LargeWidget (Customer
 * Delivery), which sits directly above it.
 */
const GigsWidget: React.FC<GigsWidgetProps> = ({ item }) => {
  const navigation = useNavigation() as unknown as GigsNavigation;
  const isLoadingDays = !!item?.loading?.includes("days");
  const days = item?.days ?? [];
  const todayIso = localTodayIso();

  const handlePress = () => {
    if (!item?.nav_to) return;
    analytics.trackEvent("Gigs Widget Clicked", { source: "Home Page Widget" });
    navigation.navigate(item.nav_to, { nav_params: item.nav_params });
  };

  return (
    <TouchableOpacity
      testID="gigs-home-widget"
      style={styles.container}
      activeOpacity={0.8}
      disabled={!item?.nav_to}
      onPress={handlePress}
    >
      <View style={styles.header}>
        <Text variant="title_20">{item?.title || GIGS_COPY.home.title}</Text>
        <View style={styles.chevronContainer}>
          <ChevronRight
            pathFill={color.lightMediumBlack}
            style={styles.chevronIcon}
          />
        </View>
      </View>
      <View style={styles.divider} />

      <Text variant="body_text_3" style={styles.subtitle}>
        {GIGS_COPY.home.subtitle(item?.window_size ?? 3)}
      </Text>

      <LoadingComponent visible={!isLoadingDays} style={styles.loadingRows}>
        <View style={styles.rows}>
          {days.map((day) => (
            <View key={day.gig_date} style={styles.row}>
              <Text
                variant="body_text_3"
                style={styles.rowDate}
                numberOfLines={1}
              >
                {formatGigDayLabel(day.gig_date, todayIso)}
              </Text>
              <View style={styles.rowStatus}>
                <Text variant="caption_2" style={styles.rowStatusLabel}>
                  {STATUS_LABELS[day.status]}
                </Text>
                <View
                  style={[
                    styles.dot,
                    { backgroundColor: getHomeWidgetDotColor(day.status) },
                  ]}
                />
              </View>
            </View>
          ))}
        </View>
      </LoadingComponent>
    </TouchableOpacity>
  );
};

export default GigsWidget;

const styles = StyleSheet.create({
  container: {
    // Same width/padding/radius/border as LargeWidget (the Customer Delivery
    // card) so the two line up.
    width: width - scale(32),
    borderWidth: 1,
    borderColor: color.black02,
    borderRadius: 24,
    marginHorizontal: 6,
    marginVertical: 8,
    padding: 12,
    overflow: "hidden",
    backgroundColor: color.white,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingLeft: 4,
    marginBottom: 4,
  },
  chevronContainer: {
    backgroundColor: color.lightGrayWhite,
    alignSelf: "flex-start",
    padding: 3,
    borderRadius: 4,
  },
  chevronIcon: {
    height: 16,
    width: 16,
  },
  divider: {
    backgroundColor: color.shadowLighter,
    height: 2,
    width: "100%",
    marginVertical: 8,
  },
  subtitle: {
    color: color.mediumBlacklightBlack,
    marginLeft: 4,
  },
  loadingRows: {
    height: 84,
    marginTop: 8,
    width: "100%",
  },
  rows: {
    marginTop: 4,
    marginLeft: 4,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 6,
  },
  rowDate: {
    color: color.mediumBlack,
    flex: 1,
    flexShrink: 1,
  },
  rowStatus: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    // Never shrink, so a long date label can't clip the status.
    flexShrink: 0,
  },
  rowStatusLabel: {
    color: color.lightMediumBlack,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
});
